import axiosConfig from "./axiosConfig";

const getSiteReport = async (
  authToken: string,
  siteId: string,
  siteName: string,
  reportName: string,
  options?: Record<string, any>,
  fileName?: string,
  esysCount?: number,
  userOrgId?: string
): Promise<void> => {
  try {
    const response = await axiosConfig.get(
      `/site/${siteId}/report?reportName=${reportName}${options ? `&options=${JSON.stringify(options)}` : ""}${userOrgId ? `&userOrgId=${userOrgId}` : ""}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        responseType: "blob",
      }
    );

    // Check if the response is an error (JSON content-type indicates an error response)
    const contentType = response.headers["content-type"];
    if (
      typeof contentType === "string" &&
      contentType.includes("application/json")
    ) {
      const text = await response.data.text();
      let errorMessage = "Failed to download report";
      try {
        const parsed = JSON.parse(text);
        errorMessage = parsed.message || parsed.error || text;
      } catch {
        errorMessage = text;
      }
      throw new Error(errorMessage);
    }

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;

    // Set the download attribute with a default file name
    const currentDateTime = new Date()
      .toISOString()
      .replace(/:/g, "-")
      .replace(/\..+/, "");

    // Sanitize site name - lowercase, replace all space with dash, remove special characters, replace repeated dashes with a single dash
    const sanitizedSiteName = siteName
      .toLowerCase()
      .trim()
      .replace(/ /g, "-")
      .replace(/[^a-zA-Z0-9-]/g, "")
      .replace(/-+/g, "-");

    // Build options string from all keys in options, with the keys sorted by name
    // in the format value1-value2
    const fileNameOptions = options || {};
    // Excel doesn't like long filenames, giving an error like "The file name is too long. Rename the file with less than 207 characters."
    // So we shorten the esysUUids to first 8 characters
    if (fileNameOptions["buyerSiteEsysUuid"]) {
      fileNameOptions["buyerSiteEsysUuid"] = fileNameOptions[
        "buyerSiteEsysUuid"
      ].substring(0, 8);
    }
    if (fileNameOptions["sellerSiteEsysUuid"]) {
      fileNameOptions["sellerSiteEsysUuid"] = fileNameOptions[
        "sellerSiteEsysUuid"
      ].substring(0, 8);
    }
    const hideSiteId =
      fileNameOptions["buyerSiteEsysUuid"] &&
      fileNameOptions["sellerSiteEsysUuid"];
    const optionsString = Object.keys(fileNameOptions)
      .sort()
      .map((key) =>
        typeof options?.[key] === "boolean"
          ? `${key}-${options?.[key]}`
          : `${options?.[key]}`
      )
      .join("-");

    // Add esysIdx suffix if present
    const esysIdxSuffix =
      options?.esysIdx && esysCount && esysCount > 1
        ? `-esys-${options.esysIdx}`
        : "";

    const extension =
      typeof contentType === "string" &&
      contentType.indexOf("officedocument.spreadsheetml.sheet") > -1
        ? "xlsx"
        : typeof contentType === "string" &&
            contentType.indexOf("text/csv") > -1
          ? "csv"
          : "docx";

    link.setAttribute(
      "download",
      `${sanitizedSiteName}-${fileName}${optionsString ? `-${optionsString}` : ""}-${currentDateTime}-${hideSiteId ? "" : siteId}${esysIdxSuffix}.${extension}`
    );

    // Append the link to the body
    document.body.appendChild(link);

    // Programmatically click the link to trigger the download
    link.click();

    // Clean up and remove the link
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
    window.URL.revokeObjectURL(url);
  } catch (err: any) {
    // Handle blob error bodies from axios errors
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        err.response.data = JSON.parse(text);
      } catch {
        err.response.data = await err.response.data.text().catch(() => null);
      }
    }
    throw err;
  }
};

export default getSiteReport;
